import { useCallback } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Plus, X } from 'lucide-react'
import type { DragEndEvent } from '@dnd-kit/core'
import type { ReactNode } from 'react'

import { PHONE_MAX } from '@/components/retail/home-form-model'
import { Button } from '@/components/ui/button'
import { FieldError } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { m } from '@/lib/paraglide/messages'

// Mảnh form dùng chung các khối trang chủ. Kéo thả theo cùng cách group-form
// (PointerSensor + KeyboardSensor: tay cầm ⋮⋮ là nút, Space/mũi tên để kéo).

/**
 * Mảng trong form (dòng, giấy, chi nhánh, SĐT…) đọc bằng useWatch, ghi lại cả
 * mảng bằng setValue — như group-form, không dùng useFieldArray vì id tự sinh
 * của nó đè lên `id` thật của dòng.
 */
export function useArrayField<T>(name: string): [T[], (next: T[]) => void] {
    const { control, setValue } = useFormContext()
    const list = (useWatch({ control, name }) as T[] | undefined) ?? []
    const set = useCallback(
        (next: T[]) => setValue(name, next, { shouldDirty: true }),
        [name, setValue],
    )
    return [list, set]
}

/** Danh sách kéo thả theo `key` của từng dòng. */
export function SortableList<T extends { key: string }>({
    rows,
    onChange,
    children,
}: {
    rows: T[]
    onChange: (rows: T[]) => void
    children: ReactNode
}) {
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    )
    const onDragEnd = (e: DragEndEvent) => {
        if (!e.over || e.active.id === e.over.id) return
        const from = rows.findIndex((r) => r.key === e.active.id)
        const to = rows.findIndex((r) => r.key === e.over!.id)
        onChange(arrayMove(rows, from, to))
    }
    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
        >
            <SortableContext
                items={rows.map((r) => r.key)}
                strategy={verticalListSortingStrategy}
            >
                {children}
            </SortableContext>
        </DndContext>
    )
}

/** Một dòng kéo được; `handle` là tay cầm ⋮⋮ (ẩn khi chỉ xem). */
export function SortableRow({
    id,
    label,
    canWrite,
    className,
    children,
}: {
    id: string
    /** Nhãn đọc màn hình của tay cầm. */
    label: string
    canWrite: boolean
    className?: string
    children: ReactNode
}) {
    const sortable = useSortable({ id, disabled: !canWrite })
    return (
        <div
            ref={sortable.setNodeRef}
            style={{
                transform: CSS.Transform.toString(sortable.transform),
                transition: sortable.transition,
            }}
            className={cn(
                'flex items-start gap-2',
                sortable.isDragging && 'relative z-10 bg-muted',
                className,
            )}
        >
            {canWrite && (
                <button
                    type="button"
                    className="mt-1 flex size-8 shrink-0 cursor-grab touch-none items-center justify-center rounded text-muted-foreground hover:bg-muted focus-visible:outline focus-visible:outline-2"
                    aria-label={m.shop_drag_aria({ name: label })}
                    {...sortable.attributes}
                    {...sortable.listeners}
                >
                    <GripVertical className="size-4" />
                </button>
            )}
            <div className="min-w-0 flex-1">{children}</div>
        </div>
    )
}

/**
 * Ô chọn (Select) gắn form; `noneLabel` = cho phép bỏ trống. Không có
 * `noneLabel` mà giá trị trống thì hiện `placeholder` (bắt chọn). Ô lỗi có
 * aria-invalid để form đưa tới đúng ô (focusFirstInvalid).
 */
export function ChoiceField({
    name,
    label,
    options,
    noneLabel,
    placeholder,
}: {
    name: string
    label: string
    options: { value: string; label: string }[]
    noneLabel?: string
    placeholder?: string
}) {
    const { control } = useFormContext()
    return (
        <div className="space-y-1.5">
            <Label htmlFor={name}>{label}</Label>
            <Controller
                control={control}
                name={name}
                render={({ field, fieldState }) => (
                    <Select
                        value={
                            (field.value as string) ||
                            (noneLabel !== undefined ? 'none' : '')
                        }
                        onValueChange={(v) =>
                            field.onChange(v === 'none' ? '' : v)
                        }
                    >
                        <SelectTrigger
                            id={name}
                            className="w-full"
                            aria-invalid={fieldState.invalid || undefined}
                        >
                            <SelectValue placeholder={placeholder} />
                        </SelectTrigger>
                        <SelectContent>
                            {noneLabel !== undefined && (
                                <SelectItem value="none">
                                    {noneLabel}
                                </SelectItem>
                            )}
                            {options.map((o) => (
                                <SelectItem key={o.value} value={o.value}>
                                    {o.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
            />
            <FieldError name={name} />
        </div>
    )
}

/** Lịch hiện từ – đến (giờ máy); trống = luôn hiện. `prefix` vd "items.0.". */
export function ScheduleFields({ prefix }: { prefix: string }) {
    const { register } = useFormContext()
    const from = `${prefix}starts_at`
    const to = `${prefix}ends_at`
    return (
        <fieldset className="space-y-2">
            <legend className="text-sm font-medium">
                {m.shop_home_schedule()}
            </legend>
            <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                    <Label htmlFor={from}>{m.shop_home_schedule_start()}</Label>
                    <Input
                        id={from}
                        type="datetime-local"
                        {...register(from)}
                    />
                    <FieldError name={from} />
                </div>
                <div className="space-y-1.5">
                    <Label htmlFor={to}>{m.shop_home_schedule_end()}</Label>
                    <Input id={to} type="datetime-local" {...register(to)} />
                    <FieldError name={to} />
                </div>
            </div>
            <p className="text-xs text-muted-foreground">
                {m.shop_home_schedule_hint()}
            </p>
        </fieldset>
    )
}

/** Danh sách SĐT (tối đa 5); lỗi từng ô theo phones.N như server trả. */
export function PhonesField({
    name,
    label,
    canWrite,
}: {
    name: string
    label: string
    canWrite: boolean
}) {
    const { register } = useFormContext()
    const [phones, setPhones] = useArrayField<string>(name)
    return (
        <div className="space-y-1.5">
            <p className="text-sm font-medium">{label}</p>
            {phones.map((_, i) => (
                <div key={i}>
                    <div className="flex gap-2">
                        <Input
                            id={`${name}.${i}`}
                            inputMode="tel"
                            aria-label={m.shop_home_phone_aria({
                                label,
                                n: String(i + 1),
                            })}
                            {...register(`${name}.${i}`)}
                        />
                        {canWrite && (
                            <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                aria-label={m.shop_home_phone_remove_aria({
                                    label,
                                    n: String(i + 1),
                                })}
                                onClick={() =>
                                    setPhones(phones.filter((__, j) => j !== i))
                                }
                            >
                                <X className="size-4" />
                            </Button>
                        )}
                    </div>
                    <FieldError name={`${name}.${i}`} />
                </div>
            ))}
            {canWrite && phones.length < PHONE_MAX && (
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setPhones([...phones, ''])}
                >
                    <Plus className="mr-1 size-4" />
                    {m.shop_home_phone_add()}
                </Button>
            )}
            <FieldError name={name} />
        </div>
    )
}
